import React, { useCallback } from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { toastError, toastSuccess } from '@/lib/toast';
import Icon from '@mdi/react';
import {
    mdiLoading,
    mdiDownload,
    mdiRouterWireless,
    mdiUpdate,
    mdiCheckCircleOutline,
    mdiAlertCircleOutline,
    mdiDotsVertical,
    mdiPlusBoxOutline,
    mdiReload,
    mdiPencil,
} from '@mdi/js';

import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import AlertBox from '@/components/layout/AlertBox';
import StatusDot from '@/components/layout/StatusDot';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Modal from '@/components/layout/Modal';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import BotaoDropdown from '@/components/BotaoDropdown';
import useWindowSize from '@/lib/useWindowSize';
import { useAuth } from '@/lib/useAuth';
import { extractApiError } from '@/lib/apiError';

import { FormAppliance } from '../appliances/FormAppliance';
import AgentDownloadModal from '../appliances/AgentDownloadModal';
import InstallAgentModal from '../appliances/InstallAgentModal';
import AdotarAgenteModal, { type AdoptionPrefill } from './AdotarAgenteModal';
import AgenteModal from './AgenteModal';

import type { AgenteDTO, EnrollmentTokenDTO } from '@webip/shared/src/dto/agente';
import type { ApplianceDTO } from '@webip/shared/src/dto/appliance';

const agentTypeMeta = {
    mikrotik: {
        label: 'MikroTik',
        color: 'var(--tc-text-color-info-emphasis)',
        background: 'var(--tc-background-color-info-subtle)',
    },
    opnsense: {
        label: 'OPNSense',
        color: 'var(--tc-text-color-warning-emphasis)',
        background: 'var(--tc-background-color-warning-subtle)',
    },
} as const;

const tokenStatusMeta: Record<EnrollmentTokenDTO['status'], { label: string; color: string; background: string }> = {
    ativo: { label: 'Ativo', color: 'var(--tc-text-color-success-emphasis)', background: 'var(--tc-background-color-success-subtle)' },
    esgotado: { label: 'Esgotado', color: 'var(--tc-color-gray-700)', background: 'var(--tc-color-gray-200)' },
    expirado: { label: 'Expirado', color: 'var(--tc-color-gray-700)', background: 'var(--tc-color-gray-200)' },
    revogado: { label: 'Revogado', color: 'var(--tc-text-color-danger-emphasis)', background: 'var(--tc-background-color-danger-subtle)' },
};

const Pill = ({ label, color, background, title }: { label: string; color: string; background: string; title?: string }) => (
    <span
        title={title}
        style={{
            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px',
            color, backgroundColor: background, whiteSpace: 'nowrap',
        }}
    >
        {label}
    </span>
);

/** "há 3 h", "em 12 min" — a informação útil aqui é a distância até agora,
 *  não o timestamp: quanto tempo o agente está de pé, quanto falta pro token
 *  morrer. O valor absoluto vai no `title`, pra quem precisar dele. */
function formatDistancia(value?: string | null): string {
    if (!value) return '—';
    const diffMs = new Date(value).getTime() - Date.now();
    const futuro = diffMs > 0;
    const abs = Math.abs(diffMs);
    const min = Math.round(abs / 60_000);
    if (min < 1) return futuro ? 'em instantes' : 'agora';
    const texto = min < 60
        ? `${min} min`
        : min < 60 * 24
            ? `${Math.round(min / 60)} h`
            : `${Math.round(min / (60 * 24))} d`;
    return futuro ? `em ${texto}` : `há ${texto}`;
}

function formatAbsoluto(value?: string | null): string | undefined {
    return value ? new Date(value).toLocaleString() : undefined;
}

const applianceVazia = (agentId: string, vendor: 'mikrotik' | 'opnsense', nome?: string, endereco?: string): ApplianceDTO => ({
    agent_type: vendor,
    agent_id: agentId,
    nome: nome ?? '',
    descricao: '',
    enderecos: [endereco ?? ''],
    usuario: '',
    senha: '',
    snmp_enabled: false,
    snmp_version: '2c',
    snmp_port: 161,
    snmp_community: '',
    redesLan: [],
    redesWan: [],
    ipsecs: [],
    dispositivos: [],
    portas: [],
} as ApplianceDTO);

const Page = () => {
    const [, , isMobile] = useWindowSize();
    // Os tokens de instalação são ADMIN-only no backend (/enrollment_token).
    // Um GESTOR ainda vê a lista de agentes — esconder a seção evita um erro
    // 403 apresentado como falha da tela inteira.
    const { user } = useAuth();
    const isAdmin = user?.papel === 'ADMIN';
    const [agentDownloadOpen, setAgentDownloadOpen] = React.useState(false);
    const [installAgentId, setInstallAgentId] = React.useState<string | null>(null);
    const [installOpen, setInstallOpen] = React.useState(false);
    const [adotando, setAdotando] = React.useState<AgenteDTO | null>(null);
    const [editando, setEditando] = React.useState<string | null>(null);
    const [model, setModel] = React.useState<ApplianceDTO | null>(null);
    const [fieldErrors, setFieldErrors] = React.useState({});
    const [mostrarEncerrados, setMostrarEncerrados] = React.useState(false);

    const queryAgentes = useQuery({
        queryKey: ['agentes'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: AgenteDTO[] }>('/api/agente', { withCredentials: true });
            return res.data.result;
        },
        refetchInterval: 10_000,
    });

    const queryLatestVersion = useQuery({
        queryKey: ['agent-latest-version'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; version: string | null }>('/api/agent/release', { withCredentials: true });
            return res.data.version;
        },
        refetchInterval: 60_000,
    });

    const queryTokens = useQuery({
        queryKey: ['enrollment-tokens', mostrarEncerrados],
        enabled: isAdmin,
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: EnrollmentTokenDTO[] }>(
                `/api/enrollment_token${mostrarEncerrados ? '?encerrados=1' : ''}`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        refetchInterval: 30_000,
    });

    const mutUpdate = useMutation({
        mutationFn: async (agentId: string) => axios.post(`/api/agente/${agentId}/update`, {}, { withCredentials: true }),
        onSuccess: () => toastSuccess('Pedido de atualização enviado ao agente.'),
        onError: (error: unknown) => {
            const { message } = extractApiError(error, 'Erro ao pedir atualização ao agente');
            toastError(message);
        },
    });

    const mutRevogar = useMutation({
        mutationFn: async (id: number) => axios.delete(`/api/enrollment_token/${id}`, { withCredentials: true }),
        onSuccess: () => {
            queryTokens.refetch();
            toastSuccess('Token revogado.');
        },
        onError: (error: unknown) => {
            const { message } = extractApiError(error, 'Erro ao revogar o token');
            toastError(message);
        },
    });

    const mutSalvarAppliance = useMutation({
        mutationFn: async (appliance: ApplianceDTO) => axios.post('/api/appliance', appliance, { withCredentials: true }),
        onSuccess: () => {
            queryAgentes.refetch();
            setModel(null);
            setFieldErrors({});
            toastSuccess('Appliance criada — o agente já está vinculado a ela.');
        },
        onError: (error: unknown) => {
            const { message, fieldErrors: errs } = extractApiError(error, 'Erro ao salvar appliance');
            if (errs) setFieldErrors(errs);
            toastError(message);
        },
    });

    const agentes = queryAgentes.data ?? [];
    const totais = React.useMemo(() => {
        const base = { online: 0, offline: 0, naoAdotados: 0 };
        for (const a of agentes) {
            if (a.online) base.online += 1; else base.offline += 1;
            if (!a.adotado) base.naoAdotados += 1;
        }
        return base;
    }, [agentes]);

    const tokensAtivos = (queryTokens.data ?? []).filter((t) => t.status === 'ativo').length;

    const handleEscolherAdocao = useCallback((prefill: AdoptionPrefill) => {
        setAdotando(null);
        setFieldErrors({});
        setModel(applianceVazia(prefill.agentId, prefill.vendor, prefill.nome, prefill.endereco));
    }, []);

    const headers: DataTableHeader[] = [
        {
            column: 'agente',
            label: 'Agente',
            mobileOrder: 0,
            render: (row: AgenteDTO) => {
                const meta = agentTypeMeta[row.agent_type] ?? agentTypeMeta.mikrotik;
                return (
                    <VBox style={{ gap: '4px', alignItems: 'flex-start' }}>
                        <HBox style={{ gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <StatusDot
                                status={row.online ? 'online' : 'offline'}
                                showLabel={false}
                                title={row.online ? 'Conectado ao backend' : 'Sem conexão WebSocket'}
                            />
                            <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{row.agent_id}</span>
                            <Pill label={meta.label} color={meta.color} background={meta.background} />
                            {row.compartilhado && (
                                <Pill
                                    label="Compartilhado"
                                    color="var(--tc-text-color-info-emphasis)"
                                    background="var(--tc-background-color-info-subtle)"
                                    title="Agente do provedor, rodando perto do backend (modo agentless) — disponível a todos os tenants"
                                />
                            )}
                            {!row.adotado && (
                                <Pill
                                    label="Não adotado"
                                    color="var(--tc-text-color-warning-emphasis)"
                                    background="var(--tc-background-color-warning-subtle)"
                                    title="Conectado e autenticado, mas ainda sem nenhuma appliance vinculada"
                                />
                            )}
                        </HBox>
                        <span style={{ fontSize: '11px', color: 'var(--tc-color-gray-600)' }}>
                            {row.install_type ? `Instalação: ${row.install_type}` : 'Instalação: desconhecida'}
                        </span>
                    </VBox>
                );
            },
        },
        {
            column: 'versao',
            label: 'Versão',
            mobileOrder: 1,
            render: (row: AgenteDTO) => {
                if (!row.online) return <span style={{ color: 'var(--tc-color-gray-600)' }}>—</span>;
                const latest = queryLatestVersion.data;
                const desatualizado = !!latest && !!row.version && row.version !== latest;
                return (
                    <HBox style={{ gap: '6px', alignItems: 'center' }}>
                        <span>{row.version ?? '—'}</span>
                        {latest && (
                            <Icon
                                path={desatualizado ? mdiUpdate : mdiCheckCircleOutline}
                                size="16px"
                                color={desatualizado ? 'var(--tc-color-warning-500)' : 'var(--tc-color-gray-400)'}
                                title={desatualizado ? `Atualização disponível: ${latest}` : `Na versão publicada (${latest})`}
                            />
                        )}
                    </HBox>
                );
            },
        },
        {
            column: 'appliances',
            label: 'Appliances',
            mobileOrder: 2,
            render: (row: AgenteDTO) => {
                if (!row.appliances.length) {
                    return (
                        <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                            Nenhuma — aguardando adoção
                        </span>
                    );
                }
                // Um agente de MSP atende dezenas de appliances: listar todas
                // estoura a linha. A primeira identifica, o "+N" dá a escala,
                // e o title (hover) tem a lista inteira pra quem precisar.
                const [primeira, ...resto] = row.appliances;
                return (
                    <HBox
                        style={{ gap: '6px', alignItems: 'baseline', flexWrap: 'wrap' }}
                        title={row.appliances.map((a) => a.nome).join(', ')}
                    >
                        <span style={{ fontSize: '13px' }}>{primeira.nome}</span>
                        {resto.length > 0 && (
                            <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)', whiteSpace: 'nowrap' }}>
                                (+{resto.length})
                            </span>
                        )}
                    </HBox>
                );
            },
        },
        {
            column: 'conexao',
            label: 'Conectado',
            mobileOrder: 3,
            render: (row: AgenteDTO) => (
                <span title={formatAbsoluto(row.connected_at)} style={{ fontSize: '13px' }}>
                    {row.online ? formatDistancia(row.connected_at) : 'offline'}
                </span>
            ),
        },
        {
            column: 'acoes',
            label: 'Ações',
            mobileOrder: 4,
            render: (row: AgenteDTO) => {
                // Só a instalação bare-metal se auto-atualiza pelo manifest
                // assinado; container/pacote é atualizado por fora.
                const podeAtualizar = row.online && row.install_type === 'bare-metal';
                // Agente compartilhado: host e tokens são definidos no env do
                // backend — nada aqui a editar, e instalador não se aplica.
                const ehCompartilhado = !!row.compartilhado;
                return (
                    <HBox style={{ gap: '8px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                        {!row.adotado && (
                            <Botao
                                size="sm"
                                variant="primary"
                                icon={mdiPlusBoxOutline}
                                onClick={() => setAdotando(row)}
                                title="Vincular este agente a uma appliance"
                            >
                                Adotar
                            </Botao>
                        )}
                        <Botao
                            size="sm"
                            variant="outline-primary"
                            icon={mdiPencil}
                            onClick={ehCompartilhado ? undefined : () => setEditando(row.agent_id)}
                            disabled={ehCompartilhado}
                            title={ehCompartilhado
                                ? 'Agente compartilhado: host e tokens vêm do env do backend'
                                : 'Editar agente (IP, tokens, instalação)'}
                        />
                        <BotaoDropdown
                            size="sm"
                            variant="outline-secondary"
                            icon={mdiDotsVertical}
                            noArrow
                            position="auto"
                            title="Mais ações"
                        >
                            <VBox style={{ gap: '2px' }}>
                                {([
                                    {
                                        icon: mdiUpdate,
                                        label: podeAtualizar
                                            ? 'Atualizar agente'
                                            : row.online
                                                ? `Atualizar agente (${row.install_type ?? 'tipo desconhecido'} — manual)`
                                                : 'Atualizar agente (offline)',
                                        onClick: () => podeAtualizar ? mutUpdate.mutate(row.agent_id) : undefined,
                                        disabled: !podeAtualizar,
                                    },
                                    {
                                        icon: mdiReload,
                                        label: 'Gerar novo instalador para este agente',
                                        onClick: () => { setInstallAgentId(row.agent_id); setInstallOpen(true); },
                                        disabled: ehCompartilhado,
                                    },
                                    {
                                        icon: mdiPlusBoxOutline,
                                        label: 'Vincular a uma nova appliance',
                                        onClick: () => setAdotando(row),
                                        disabled: false,
                                    },
                                    {
                                        icon: mdiPencil,
                                        label: 'Editar agente (IP, tokens, instalação)',
                                        onClick: () => setEditando(row.agent_id),
                                        disabled: ehCompartilhado,
                                    },
                                ] as const).map((item) => (
                                    <button
                                        key={item.label}
                                        type="button"
                                        onClick={item.onClick}
                                        disabled={item.disabled}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            padding: '8px', borderRadius: '8px',
                                            background: 'none', border: 'none',
                                            cursor: item.disabled ? 'not-allowed' : 'pointer',
                                            color: item.disabled ? 'var(--tc-color-gray-400)' : 'var(--tc-color-gray-700)',
                                            width: '100%', textAlign: 'left', fontSize: '14px', whiteSpace: 'nowrap',
                                            opacity: item.disabled ? 0.5 : 1,
                                        }}
                                        onMouseEnter={(e) => { if (!item.disabled) e.currentTarget.style.backgroundColor = 'var(--tc-color-gray-200)'; }}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        <Icon path={item.icon} size="18px" />
                                        {item.label}
                                    </button>
                                ))}
                            </VBox>
                        </BotaoDropdown>
                    </HBox>
                );
            },
        },
    ];

    const tokenHeaders: DataTableHeader[] = [
        {
            column: 'agent_id',
            label: 'Agente alvo',
            mobileOrder: 0,
            render: (row: EnrollmentTokenDTO) => {
                const meta = tokenStatusMeta[row.status];
                return (
                    <VBox style={{ gap: '4px', alignItems: 'flex-start' }}>
                        <HBox style={{ gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{row.agent_id}</span>
                            <Pill label={meta.label} color={meta.color} background={meta.background} />
                        </HBox>
                        <span style={{ fontSize: '11px', color: 'var(--tc-color-gray-600)' }}>
                            {row.alvo_tipo === 'novo_agente' ? 'Agente novo' : 'Agente já existente'}
                            {row.site_tag ? ` · ${row.site_tag}` : ''}
                        </span>
                    </VBox>
                );
            },
        },
        {
            column: 'usos',
            label: 'Usos',
            mobileOrder: 1,
            render: (row: EnrollmentTokenDTO) => <span>{row.usos} / {row.max_usos}</span>,
        },
        {
            column: 'expira_em',
            label: 'Expira',
            mobileOrder: 2,
            render: (row: EnrollmentTokenDTO) => (
                <span title={formatAbsoluto(row.expira_em)}>{formatDistancia(row.expira_em)}</span>
            ),
        },
        {
            column: 'criado',
            label: 'Criado',
            mobileOrder: 3,
            render: (row: EnrollmentTokenDTO) => (
                <VBox style={{ gap: '2px' }}>
                    <span title={formatAbsoluto(row.data_criacao)}>{formatDistancia(row.data_criacao)}</span>
                    <span style={{ fontSize: '11px', color: 'var(--tc-color-gray-600)' }}>
                        {row.criado_por_nome ?? 'origem desconhecida'}
                    </span>
                </VBox>
            ),
        },
        {
            column: 'acoes',
            label: 'Ações',
            mobileOrder: 4,
            render: (row: EnrollmentTokenDTO) => (
                row.status === 'ativo' ? (
                    <BotaoPerigoso
                        size="sm"
                        variant="outline-danger"
                        title="Revogar token"
                        confirmation={`Revogar o token de instalação de ${row.agent_id}? Quem ainda não usou o comando gerado com ele não vai conseguir instalar.`}
                        onConfirm={() => mutRevogar.mutate(row.id)}
                    >
                        Revogar
                    </BotaoPerigoso>
                ) : (
                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>—</span>
                )
            ),
        },
    ];

    return (
        <LayoutUsuario>
            <VBox style={{ padding: isMobile ? '16px' : '24px', gap: '16px', height: '100%', overflow: 'auto' }}>
                <PageHeader
                    title="Agentes"
                    description="Instalações do adapter que conectam os equipamentos à plataforma"
                    actions={(
                        <>
                            <Botao
                                icon={mdiDownload}
                                onClick={() => setAgentDownloadOpen(true)}
                                variant="outline-secondary"
                                size="md"
                                title="Download manual do agente (avançado)"
                                style={{ flex: isMobile ? '1 1 160px' : undefined }}
                            >
                                Download
                            </Botao>
                            <Botao
                                icon={mdiRouterWireless}
                                onClick={() => { setInstallAgentId(null); setInstallOpen(true); }}
                                variant="primary"
                                size="md"
                                style={{ flex: isMobile ? '1 1 160px' : undefined }}
                            >
                                Instalar Agente
                            </Botao>
                        </>
                    )}
                />

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '140px' : '180px'}, 1fr))`,
                        gap: '12px',
                        width: '100%',
                    }}
                >
                    {([
                        { key: 'online', label: 'Online', value: totais.online, color: 'var(--telemetry-summary-online-color)', background: 'var(--telemetry-summary-online-bg)' },
                        { key: 'offline', label: 'Offline', value: totais.offline, color: 'var(--telemetry-summary-offline-color)', background: 'var(--telemetry-summary-offline-bg)' },
                        { key: 'pendentes', label: 'Aguardando adoção', value: totais.naoAdotados, color: 'var(--telemetry-summary-degraded-color)', background: 'var(--telemetry-summary-degraded-bg)' },
                        { key: 'tokens', label: 'Tokens ativos', value: tokensAtivos, color: 'var(--telemetry-summary-unknown-color)', background: 'var(--telemetry-summary-unknown-bg)' },
                    ] as const).map((item) => (
                        <VBox
                            key={item.key}
                            style={{
                                minWidth: 0, padding: '12px 14px', borderRadius: '12px',
                                border: '1px solid var(--border-1)', background: item.background, gap: '4px',
                            }}
                        >
                            <span style={{ fontSize: '12px', fontWeight: 600, color: item.color }}>{item.label}</span>
                            <span style={{ fontSize: '24px', fontWeight: 700, color: 'var(--color-body)' }}>{item.value}</span>
                        </VBox>
                    ))}
                </div>

                {totais.naoAdotados > 0 && (
                    <AlertBox variant="warning">
                        <HBox style={{ gap: '8px', alignItems: 'center' }}>
                            <Icon path={mdiAlertCircleOutline} size="18px" />
                            <span>
                                {totais.naoAdotados === 1
                                    ? '1 agente conectado ainda não está vinculado a nenhuma appliance.'
                                    : `${totais.naoAdotados} agentes conectados ainda não estão vinculados a nenhuma appliance.`}
                                {' '}Use "Adotar" para cadastrar o equipamento que ele opera.
                            </span>
                        </HBox>
                    </AlertBox>
                )}

                {queryAgentes.isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                        <Icon path={mdiLoading} spin size={2} />
                    </HBox>
                )}

                {queryAgentes.isError && (
                    <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                        <p>Erro ao carregar os agentes. Por favor, tente novamente.</p>
                    </HBox>
                )}

                {!queryAgentes.isLoading && !queryAgentes.isError && (
                    agentes.length === 0 ? (
                        <AlertBox variant="info">
                            Nenhum agente ainda. Clique em "Instalar Agente" para gerar o comando de instalação — o agente
                            conecta sozinho na plataforma, sem abrir porta na rede do cliente.
                        </AlertBox>
                    ) : (
                        <DataTable cabecalhos={headers} linhas={agentes} />
                    )
                )}

                {isAdmin && (
                <HBox style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap', gap: '8px' }}>
                    <VBox style={{ gap: '2px' }}>
                        <span style={{ fontSize: '16px', fontWeight: 600 }}>Tokens de instalação</span>
                        <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                            Credenciais de uso único (ou limitado) que autorizam um agente a se registrar. O valor em si
                            só apareceu uma vez, no comando gerado — aqui dá pra acompanhar e revogar.
                        </span>
                    </VBox>
                    <Botao
                        variant="none-secondary"
                        size="sm"
                        onClick={() => setMostrarEncerrados((v) => !v)}
                    >
                        {mostrarEncerrados ? 'Mostrar só os ativos' : 'Mostrar encerrados também'}
                    </Botao>
                </HBox>
                )}

                {isAdmin && (
                    queryTokens.isError ? (
                        <AlertBox variant="danger">Erro ao carregar os tokens de instalação.</AlertBox>
                    ) : (queryTokens.data?.length ?? 0) === 0 ? (
                        <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                            {mostrarEncerrados ? 'Nenhum token registrado.' : 'Nenhum token ativo no momento.'}
                        </span>
                    ) : (
                        <DataTable cabecalhos={tokenHeaders} linhas={queryTokens.data ?? []} />
                    )
                )}
            </VBox>

            <AgentDownloadModal show={agentDownloadOpen} onClose={() => setAgentDownloadOpen(false)} />

            <InstallAgentModal
                show={installOpen}
                agentId={installAgentId ?? undefined}
                onClose={() => setInstallOpen(false)}
                onAgentConnected={({ agentId, vendor, endereco, nome }) => {
                    setInstallOpen(false);
                    setFieldErrors({});
                    setModel(applianceVazia(agentId, vendor, nome, endereco));
                }}
            />

            <AdotarAgenteModal
                agente={adotando}
                onClose={() => setAdotando(null)}
                onEscolher={handleEscolherAdocao}
            />

            <AgenteModal
                agentId={editando}
                onClose={() => setEditando(null)}
                onAlterado={() => queryAgentes.refetch()}
                onGerarInstalador={(agentId) => {
                    setEditando(null);
                    setInstallAgentId(agentId);
                    setInstallOpen(true);
                }}
            />

            <Modal
                show={model !== null}
                title="Cadastrar appliance do agente"
                onClose={() => { setModel(null); setFieldErrors({}); }}
                onCancel={() => { setModel(null); setFieldErrors({}); }}
                onConfirm={() => { if (model) mutSalvarAppliance.mutate(model); }}
                confirmProps={{ label: 'Salvar', disabled: mutSalvarAppliance.isLoading }}
                size="lg"
            >
                {model && (
                    <FormAppliance
                        value={model}
                        onChange={setModel}
                        fieldErrors={fieldErrors}
                    />
                )}
            </Modal>
        </LayoutUsuario>
    );
};

export default Page;
